
import { StatsCard } from '@/components/shared/StatsCard'
import { StatsChart } from '@/components/shared/StatsChart';
import { LogStatsBarChart } from '@/components/shared/LogStatsBarChart';

import {
	File,
	Database,
	Gauge,
	Info,
	Trash,
	TrendingUpDown,
	FilePlus
} from "lucide-react";

import {
	useNetworkStats,
	useNetworkDataGrowthRate,
	useNetworkBlobSizeDistribution,
	useNetworkBlobGrowthRate,
} from '@/hooks/useApi';
import { useMemo } from 'react';


export const OverviewView = () => {

	const { data: networkStats } = useNetworkStats()
	const { data: networkDataGrowthRate } = useNetworkDataGrowthRate();
	const { data: networkBlobSizeDistribution } = useNetworkBlobSizeDistribution();
	const { data: networkBlobGrowthRate } = useNetworkBlobGrowthRate();

	const growthDataInGB = useMemo(() => {
		return networkDataGrowthRate?.data.map((item: any) => {
			return {
				DAY: item.DAY,
				TOTAL_GB: Number(item.TOTAL_BYTES / 1_000_000_000)
			}
		})
	}, [networkDataGrowthRate])

	return (
		<div className="flex-1 overflow-y-auto overflow-x-hidden flex max-w-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
			<div className='flex flex-col gap-4 w-full h-full pr-8 lg:px-4 py-5'>
				<StatsCard
					title=""
					className='text-sm'
					valueStyleOverride='text-lg lg:text-xl'
					value=""
					icon={<Info className="h-4 w-4 text-white"/>}
					description='Walrus is a decentralized storage platform that enables you to program, publish, and distribute data onchain. As a Sui-native and permissionless network, Walrus includes highly-replicated blob datasets secured by the Sui L1 blockchain. All blobs stored in Walrus are public and discoverable by all.'
				/>

				{/* Stat Cards */}
				<div
					className='grid grid-cols-1 gap-4 p-2
						md:grid-cols-2
						xl:grid-cols-4
					'>
					<StatsCard
						title="Total Blobs"
						className='text-sm'
						valueStyleOverride='text-lg lg:text-xl'
						value={Number(networkStats?.blobCertified).toLocaleString()}
						icon={<File className="h-4 w-4" />}
						description='Number of certified blobs.'
						isLoading={!networkStats}
					/>
					<StatsCard
						title="Storage Capacity"
						className='text-sm'
						valueStyleOverride='text-lg lg:text-xl'
						value={`${Number(networkStats?.utilization?.consumed / 1_000_000_000_000).toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})} / ${Number(networkStats?.utilization?.capacity / 1_000_000_000_000).toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})} TB`}
						icon={<Database className="h-4 w-4" />}
						description='The network storage capacity.'
						isLoading={!networkStats}
					/>
					<StatsCard
						title="Storage Utilization"
						className='text-sm'
						valueStyleOverride='text-lg lg:text-xl'
						value={`${Number(networkStats?.utilization?.utilization).toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})} %`}
						icon={<Gauge className="h-4 w-4" />}
						description='Percent of network capacity consumed.'
						isLoading={!networkStats}
					/>
					<StatsCard
						title="Deleted Blobs"
						className='text-sm'
						valueStyleOverride='text-lg lg:text-xl'
						value={`${networkStats?.blobDeleted.toLocaleString()}`}
						icon={<Trash className="h-4 w-4" />}
						description='The total number of deleted blobs.'
						isLoading={!networkStats}
					/>
				</div>

				{/* Stat Charts */}
				<div
					className='grid grid-cols-1 gap-4 p-2
						md:grid-cols-3 
					'
				>
					<LogStatsBarChart
						className='text-sm'
						descriptionStyleOverride='text-xs md:text-md'
						title='Blob Size Distribution (30 day)'
						data={networkBlobSizeDistribution?.data as any}
						xDataKey='BUCKET_POW'
						yDataKey='TOTAL_BLOBS'
						icon={<File className='h-4 w-4' />}
						description='The size distribution of certified blobs added to the network over the last 30 days. Log Scale.'
						isLoading={!networkBlobSizeDistribution?.data}
					/>
					<StatsChart
						className='text-sm'
						descriptionStyleOverride='text-xs md:text-md'
						title='Daily Network Data Growth (30 Day)'
						data={growthDataInGB as any}
						xDataKey='DAY'
						yDataKey='TOTAL_GB'
						icon={<TrendingUpDown className='h-4 w-4' />}
						description='The amount of data added to the network over the last 30 days.'
						isLoading={!growthDataInGB}
					/>
					<StatsChart
						className='text-sm'
						descriptionStyleOverride='text-xs md:text-md'
						title='Daily Network Blob Growth (30 Day)'
						data={networkBlobGrowthRate?.data?.toReversed() as any}
						xDataKey='DAY'
						yDataKey='BLOBS_CERTIFIED'
						icon={<FilePlus className='h-4 w-4' />}
						description='The number of certified blobs added to the network over the last 30 days.'
						isLoading={!networkBlobGrowthRate?.data}
					/>
				</div>
			</div>
		</div>
	);
}